<template>
  <base-material-card color="primary" icon="mdi-circle-edit-outline" inline class="px-5 py-3">
    <template v-slot:after-heading>
      <div>
        Visão Geral de Justificativas - BDGD
        {{ bdgd.ordinaria ? 'Ordinária' : '' }}
        {{ bdgd.data_registro | formatToMonth }} v{{ bdgd.versao }}
      </div>
    </template>

    <v-row v-if="!loading && justificativas.length" class="mb-2 text-center">
      <v-col cols="3">
        <div class="font-weight-bold primary--text text-h4">{{ totalJustificativas }}</div>
        <div class="grey--text text-caption text-uppercase">Validações justificadas</div>
      </v-col>
      <v-col cols="3">
        <div class="font-weight-bold info--text text-h4">{{ totalLinhasJustificadas | parseNumberToIntegerBR }}</div>
        <div class="grey--text text-caption text-uppercase">Linhas justificadas</div>
      </v-col>
      <v-col cols="3">
        <div class="font-weight-bold text-h4">{{ totalEntidades }}</div>
        <div class="grey--text text-caption text-uppercase">Entidades afetadas</div>
      </v-col>
      <v-col cols="3">
        <div class="font-weight-bold text-h4">{{ totalTestes }}</div>
        <div class="grey--text text-caption text-uppercase">Etapas envolvidas</div>
      </v-col>
    </v-row>
    <v-divider v-if="!loading && justificativas.length" class="mb-4" />

    <v-row>
      <v-col cols="12">
        <div class="align-center d-flex mb-4">
          <v-text-field v-model="search" append-icon="mdi-magnify" label="Procurar" hide-details single-line dense />
          <v-btn
            class="ml-3"
            color="primary"
            outlined
            small
            :disabled="!justificativas.length"
            :loading="exportando"
            @click="exportarJustificativas"
          >
            <v-icon left small>mdi-download</v-icon>
            Exportar
          </v-btn>
        </div>
        <v-data-table
          :headers="headers"
          :items="justificativas"
          :search.sync="search"
          :loading="loading"
          :items-per-page="15"
          sort-by="entidade"
        >
          <template v-slot:[`item.sigla_teste`]="{ item }">Etapa {{ item.etapa }} - {{ item.sigla_teste }}</template>
          <template v-slot:[`item.status_anterior`]="{ item }">
            <general-status :status="item.status_anterior || 'OK'" />
          </template>
          <template v-slot:[`item.justificativa`]="{ item }">
            <span :title="item.justificativa">{{ item.justificativa }}</span>
          </template>
          <template v-slot:[`item.usuario_criacao`]="{ item }">
            <span>{{ item.usuario_atualizacao || item.usuario_criacao }}</span>
          </template>
          <template v-slot:[`item.updated_at`]="{ item }">
            {{ (item.updated_at || item.created_at) | parseToDateTimeBR }}
          </template>
          <template v-slot:[`item.acoes`]="{ item }">
            <v-btn v-if="usuarioAdmin" icon small title="Editar justificativa" @click="abrirEdicao(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              v-if="usuarioAdmin"
              icon
              small
              title="Excluir justificativa"
              :loading="excluindoId === item.id"
              @click="excluirJustificativa(item)"
            >
              <v-icon small color="red darken-1">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogEdicao" max-width="650">
      <v-card>
        <h2 class="d-flex justify-center py-4">Gestão de Justificativa</h2>
        <v-divider class="mb-2"></v-divider>
        <v-card-subtitle v-if="itemEditando" class="pt-2">
          <div class="mb-2">
            <span><strong>Entidade:</strong></span>
            {{ itemEditando.entidade }}
          </div>
          <div class="mb-2">
            <span><strong>Validação:</strong></span>
            {{ itemEditando.nome_validacao }}
          </div>
          <div class="mb-2">
            <span><strong>Tipo:</strong></span>
            {{ itemEditando.tipo_ocorrencia }}
          </div>
        </v-card-subtitle>
        <v-card-text class="pt-4">
          <form-justificativa
            v-if="dialogEdicao && itemEditando"
            :testeSelecionado="{ sigla_teste: itemEditando.sigla_teste }"
            :item="itemEditando"
            :abertoPorPadrao="true"
            @justificativa-atualizada="onJustificativaAtualizada"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogEdicao = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
  import dayjs from 'dayjs';
  import AuthService from '@/services/AuthService';
  import ValidacoesBdgdsJustificativasService from '@/services/ValidacoesBdgdsJustificativasService';
  import BdgdVersionsService from '@/services/BdgdVersionsService';

  export default {
    name: 'JustificativasOverview',
    components: {
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
      FormJustificativa: () => import('./FormJustificativa.vue')
    },
    props: {
      bdgdVersaoId: {
        type: Number,
        required: true
      }
    },
    data: () => ({
      justificativas: [],
      loading: false,
      search: '',
      usuarioAdmin: false,
      dialogEdicao: false,
      itemEditando: null,
      excluindoId: null,
      exportando: false,
      bdgd: {
        id: 0,
        data_registro: null,
        versao: 1,
        ordinaria: false
      }
    }),
    computed: {
      totalJustificativas() {
        return this.justificativas.length;
      },
      totalLinhasJustificadas() {
        return this.justificativas.reduce((sum, j) => sum + (j.qtd_erros_encontrados || 0), 0);
      },
      totalEntidades() {
        return new Set(this.justificativas.map((j) => j.entidade)).size;
      },
      totalTestes() {
        return new Set(this.justificativas.map((j) => j.sigla_teste)).size;
      },
      headers() {
        const cols = [
          { text: 'Teste', value: 'sigla_teste' },
          { text: 'Entidade', value: 'entidade', width: '110px' },
          { text: 'Campo', value: 'campo', width: '120px' },
          { text: 'Nome da validação', value: 'nome_validacao' },
          { text: 'Status do teste', value: 'status_anterior', sortable: false, width: '140px' },
          { text: 'Justificativa', value: 'justificativa', sortable: false, align: 'center' },
          { text: 'Atualizado por', value: 'usuario_criacao', width: '140px' },
          { text: 'Atualizado em', value: 'updated_at', width: '150px' }
        ];
        if (this.usuarioAdmin) {
          cols.push({ text: '', value: 'acoes', sortable: false, width: '90px' });
        }
        return cols;
      }
    },
    mounted() {
      AuthService.userIsAdmin().then((isAdmin) => (this.usuarioAdmin = isAdmin));
      this.listarJustificativas();
      this.getBdgd();
    },
    methods: {
      getBdgd() {
        if (!this.bdgdVersaoId) return false;
        BdgdVersionsService.getById(this.bdgdVersaoId)
          .then((response) => (this.bdgd = response))
          .catch((err) => {
            console.log('Erro: ', err);
            this.$toast.error('Erro ao buscar os dados da versão da BDGD selecionada.', '', { position: 'topRight' });
          });
      },
      listarJustificativas() {
        this.loading = true;
        ValidacoesBdgdsJustificativasService.listarJustificativas()
          .then(({ data }) => (this.justificativas = data))
          .catch(() => {
            this.$toast.error('Erro ao buscar as justificativas.', '', { position: 'topRight' });
          })
          .finally(() => (this.loading = false));
      },
      exportarJustificativas() {
        this.exportando = true;
        this.$toast.info('Preparando arquivo para download.', '', { position: 'topRight', timeout: 2000 });
        ValidacoesBdgdsJustificativasService.exportarJustificativas()
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            const mes = dayjs(this.bdgd.data_registro).format('YYYY-MM');
            link.setAttribute('download', `justificativas-bdgd-${mes}-v${this.bdgd.versao}.xlsx`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            this.$toast.error('Erro ao exportar as justificativas.', '', { position: 'topRight' });
          })
          .finally(() => (this.exportando = false));
      },
      abrirEdicao(item) {
        this.itemEditando = item;
        this.dialogEdicao = true;
      },
      onJustificativaAtualizada({ status }) {
        this.dialogEdicao = false;
        this.listarJustificativas();
        if (status != null && this.itemEditando) {
          const index = this.justificativas.findIndex((j) => j.id === this.itemEditando.id);
          if (index !== -1) {
            this.$set(this.justificativas[index], 'status_anterior', status);
          }
        }
      },
      excluirJustificativa(item) {
        if (!confirm('Tem certeza que deseja excluir esta justificativa?')) return;
        this.excluindoId = item.id;
        ValidacoesBdgdsJustificativasService.excluirJustificativa(item.id)
          .then(() => {
            this.$toast.success('Justificativa excluída com sucesso.', '', { position: 'topRight' });
            this.justificativas = this.justificativas.filter((j) => j.id !== item.id);
          })
          .catch(() => {
            this.$toast.error('Erro ao tentar excluir a justificativa.', '', { position: 'topRight' });
          })
          .finally(() => (this.excluindoId = null));
      }
    }
  };
</script>
